import type { RegisteredComponent } from "@builder.io/sdk-react";

import { CustomGrid } from "./custom-grid";
import { DynamicGrid } from "./dynamic-grid";
import type { ArticleGridProps } from "./types";
import { articleCardDefault } from "./utils";

/**
 * SFCC-11498 (ARTICLES-12) — port of composable `articleGrid.jsx`.
 *
 * Two modes:
 *  - `customGrid: true`  → `CustomGrid`: author hand-picks ArticleCard children.
 *  - `customGrid: false` → `DynamicGrid`: API-driven, `?topic=`-filtered list via
 *    the `/resources/article-grid` resource route.
 *
 * `noWrap` block — each mode spreads `attributes` on its root (via
 * `PaginatedGridDisplay` / the skeleton wrapper).
 */

const ArticleGrid = (props: ArticleGridProps) =>
  props.customGrid ? <CustomGrid {...props} /> : <DynamicGrid {...props} />;

export const ArticleGridDefinition = {
  component: ArticleGrid,
  name: "ArticleGrid",
  noWrap: true,
  canHaveChildren: true,
  childRequirements: {
    message: "Children of 'ArticleGrid' must be 'ArticleCard'",
    query: {
      "component.name": { $in: ["ArticleCard"] },
    },
  },
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  inputs: [
    { name: "itemsToShowPerLoad", type: "number", defaultValue: 10 },
    { name: "customGrid", type: "boolean", defaultValue: false },
    {
      name: "articleCards",
      type: "list",
      subFields: [
        {
          name: "content",
          type: "uiBlocks",
          defaultValue: [articleCardDefault],
        },
      ],
      showIf: `options.get('customGrid') === true`,
      defaultValue: [{ content: [articleCardDefault] }],
    },
  ],
} satisfies RegisteredComponent;

export default ArticleGrid;
